import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { isAdminEmail } from '@/lib/admin';
import type { ReactNode } from 'react';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Gates a route to admin users only. Unauthenticated visitors are sent to
 * login; authenticated non-admins are sent to their dashboard. This is a UX
 * gate — the API independently enforces admin access and returns 403.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="text-brand-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdminEmail(user?.email)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
